/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
  createHttpLink,
  DefaultOptions,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { relayStylePagination } from "@apollo/client/utilities";

import { store } from "store";

const authLink = setContext((_, { headers }) => {
  // get the authentication token from redux state if it exists
  const reduxState = store.getState() as any;
  const apiToken = reduxState.authToken;
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: `Token ${apiToken}`,
    },
  };
});

const primaryAuthLink = setContext((_, { headers }) => {
  // get the primary authentication token from redux state if it exists
  const reduxState = store.getState() as any;
  const apiToken = reduxState.primaryAuthToken;
  return {
    headers: {
      ...headers,
      authorization: `Token ${apiToken}`,
    },
  };
});

const createUpduoHttpLink = (isAuthenticated: boolean) =>
  createHttpLink({
    headers: {
      "upduo-version": `Web/${process.env.REACT_APP_SKALE_WEB_VERSION}`,
    },
    uri: (operation) =>
      `${process.env.REACT_APP_SERVER_URL}/api/graphql${
        isAuthenticated ? "" : "/public"
      }/?source=web&operation=${encodeURIComponent(operation.operationName)}`,
  });

const publicLink = createUpduoHttpLink(false);
const privateLink = createUpduoHttpLink(true);

const defaultOptions: DefaultOptions = {
  watchQuery: {
    errorPolicy: "ignore",
  },
  query: {
    errorPolicy: "all",
  },
};

export const generateApolloClient = (
  authLink: ApolloLink
): ApolloClient<NormalizedCacheObject> =>
  new ApolloClient({
    link: ApolloLink.split(
      (operation) => operation.getContext().isPublic,
      publicLink,
      authLink.concat(privateLink)
    ),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            staffSessions: relayStylePagination(["getStaffSessionListRequest"]),
            staffSummitFeedback: relayStylePagination([
              "getSummitFeedbackRequest",
            ]),
            staffUsers: relayStylePagination([
              "getStaffMemberUserListRequest",
              "getStaffSummitAttendanceListRequest",
            ]),
            staffUserproxies: relayStylePagination([
              "getStaffUserproxiesListRequest",
            ]),
            userPastSummits: relayStylePagination(["isStaff"]),
            kudos: relayStylePagination(["getKudosRequest"]),
            kudos2: relayStylePagination(["getKudosRequest"]),
          },
        },
      },
    }),
    defaultOptions,
  });

export const UpduoApolloClient = generateApolloClient(authLink);

export const SkalePrimaryApolloClient = generateApolloClient(primaryAuthLink);
